import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import EStyleSheet from 'react-native-extended-stylesheet';
import { MaterialIcons } from '@expo/vector-icons';

// Components
import { Button, FormControl, Icon, Input, Stack, Select, Text, TextArea } from 'native-base';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  View,
  Modal,
} from 'react-native';
import Alert from '../../components/Alert';
import DateTimePicker from '@react-native-community/datetimepicker';
import { FormattedMessage } from 'react-native-globalize';
import { Right } from '../../bbot-component-library';
import { offsetInput, offsetOutput } from '../../helpers/DateTimePickerHelper';
import API from '../../api';
import { TEXT_FIELD_MAX_LENGTH } from '../../helpers/constants';
import { confirmAction } from '../../helpers/confirmAction';
import { isCateringDraftOrder } from '../../helpers/OrderHelper';

// Styles
import { Colors, Forms } from '../../styles';
import { styles as gStyle, modalStyles } from '../../styles/Global';

function EditOrderModal({ visible, order, onSave, onCancel }) {
  const [courierCapabilities, setCourierCapabilities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [orderEdits, setOrderEdits] = useState({});
  const orderEditsRef = useRef(orderEdits);
  orderEditsRef.current = orderEdits;
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [showSpecialInstructions, setShowSpecialInstructions] = useState(true);

  const locations = useMemo(() => _.sortBy(Object.values(API._locations), ['locationName']), []);
  const bartenders = useMemo(() => _.sortBy(Object.values(API._bartenders), ['first_name']), []);
  useEffect(() => {
    let cancelled = false;
    API.getCourierCapabilities().then(caps => {
      if (!cancelled) setCourierCapabilities(caps);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeItems = useMemo(
    () => (order.items ? order.items.filter(i => i.status !== 'refunded') : []),
    [order.items],
  );

  const clearOrderEdits = () => {
    setOrderEdits({});
  };

  const executeSave = async fieldsToEdit => {
    try {
      setIsLoading(true);
      await onSave(fieldsToEdit);
    } finally {
      setIsLoading(false);
      clearOrderEdits();
    }
  };

  const displayDeliveryJobUpdateWarning = () => {
    const { driver_delivery_jobs } = order;
    if (!driver_delivery_jobs.length) return null;

    const deliveryProvider = driver_delivery_jobs[0].delivery_provider;
    if (courierCapabilities[deliveryProvider]?.update_job) return null;

    return (
      <View style={{ marginBottom: 8 }}>
        <FormattedMessage
          id="common__warning"
          defaultMessage="Warning:"
          style={{
            color: Colors.lightTheme.error.regular,
            fontWeight: 'bold',
            marginBottom: 8,
          }}
        />
        <FormattedMessage
          id="warning__pickupTimer"
          defaultMessage="The delivery provider for this order does not allow the pickup time to be automatically updated. If you change the desired pickup time, then you must contact the delivery provider to cancel or reschedule."
          style={{ color: Colors.lightTheme.error.regular }}
        />
      </View>
    );
  };

  const handleCancel = () => {
    clearOrderEdits();
    onCancel();
  };

  const isAllItemsRemovalBlocked = (itemsToRemove, activeItems) => {
    if (itemsToRemove.length < activeItems.length) return false;

    if (Platform.OS === 'web') {
      window.alert('Cannot remove all items from an order. At least one item must remain.');
    } else {
      Alert.alert('Cannot Remove All Items', 'At least one item must remain in the order.');
    }
    return true;
  };

  const serializeFields = fields => {
    if (!fields.user_desired_time || !moment.isMoment(fields.user_desired_time)) return fields;
    return { ...fields, user_desired_time: moment.utc(fields.user_desired_time).format() };
  };

  const confirmItemRemoval = async () => {
    const items = orderEditsRef.current.items_to_remove || [];
    const confirmed = await confirmAction(
      'Confirm Item Removal',
      `Remove ${items.length} item(s) from this draft order?`,
    );
    if (!confirmed) return;
    // Read the latest edits after the user confirms (avoids stale snapshot if
    // the user typed in staff notes while the confirmation dialog was open).
    await executeSave(serializeFields({ ...orderEditsRef.current }));
  };

  const handleSave = async () => {
    const fieldsToEdit = serializeFields({ ...orderEdits });

    const itemsToRemove = fieldsToEdit.items_to_remove || [];
    if (itemsToRemove.length > 0) {
      if (isAllItemsRemovalBlocked(itemsToRemove, activeItems)) {
        return;
      }
      await confirmItemRemoval();
      return;
    }

    await executeSave(fieldsToEdit);
  };

  const toggleShowDatetimePicker = () => {
    setShowDateTimePicker(prev => !prev);
  };

  const updateOrderEditsByKey = (key, value) => {
    setOrderEdits(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleItemRemoval = orderitemid => {
    setOrderEdits(prev => {
      const current = prev.items_to_remove || [];
      const updated = current.includes(orderitemid)
        ? current.filter(id => id !== orderitemid)
        : [...current, orderitemid];

      const nextEdits = { ...prev };
      if (updated.length > 0) {
        nextEdits.items_to_remove = updated;
      } else {
        delete nextEdits.items_to_remove;
      }
      return nextEdits;
    });
  };

  const updateOrderItemSpecialInstructions = ({ orderitemid, special_instructions }) => {
    setOrderEdits(prev => {
      const items = [...(prev?.items || [])];
      const indexToEdit = items.findIndex(item => item.orderitemid === orderitemid);

      if (indexToEdit >= 0) {
        items[indexToEdit] = { orderitemid, special_instructions };
      } else {
        items.push({ orderitemid, special_instructions });
      }

      return {
        ...prev,
        items,
      };
    });
  };

  const updateUserDesiredTime = (event, time) => {
    if (event.type === 'dismissed') {
      setShowDateTimePicker(false);
      return;
    }

    let user_desired_time = moment(offsetOutput(time));

    if (user_desired_time.isSameOrBefore(moment(), 'minutes')) {
      user_desired_time = null; // Convert to ASAP order.
    }

    setOrderEdits(prev => ({
      ...prev,
      user_desired_time,
    }));
    setShowDateTimePicker(false);
  };

  // --- Render ---

  const itemsToRemoveList = orderEdits.items_to_remove || [];
  const allItemsMarkedForRemoval =
    activeItems.length > 0 && itemsToRemoveList.length >= activeItems.length;
  const location_id = orderEdits.location_id || order.location_id;
  const bartender_id = orderEdits.bartender_id || order.bartender_id;
  const staff_notes =
    orderEdits.staff_notes !== undefined ? orderEdits.staff_notes : order.staff_notes || '';
  const user_desired_time =
    orderEdits.user_desired_time !== undefined
      ? orderEdits.user_desired_time
      : order.user_desired_time;

  if (!visible) return null;
  const disabledStyle = !!order.time_closed && { color: Colors.disabled };
  // Item removal is only for catering drafts (not yet paid). Non-draft orders use the refund flow.
  const showRemoveItems =
    !order.time_closed && order.items.length > 0 && isCateringDraftOrder(order);

  return (
    <Modal
      onRequestClose={handleCancel}
      transparent
      visible={visible}
      statusBarTranslucent
    >
      <TouchableWithoutFeedback onPress={handleCancel}>
        <View style={gStyle.modalBackground}>
          {/* Prevent touches from hiding modal. */}
          <TouchableWithoutFeedback>
            <View style={{ maxHeight: '80%', maxWidth: 600, width: '80%' }}>
              <View style={modalStyles.header}>
                <FormattedMessage
                  id="order__edit"
                  values={{ orderNumber: order.orderNumber }}
                  defaultMessage={`Edit Order: #${order.orderNumber}`}
                  style={[modalStyles.headerText, gStyle.textMed]}
                />
              </View>
              <ScrollView style={[modalStyles.body, { maxHeight: 'auto' }]}>
                <FormControl style={{ flex: 1, marginBottom: 5, padding: 15 }}>
                  {/* Staff Notes */}
                  <Stack
                    style={[Forms.formField]}
                    accessible={false}
                  >
                    <View style={{ width: '100%' }}>
                      <FormControl.Label style={styles.formField__label}>
                        <FormattedMessage
                          id="order__staffNotes"
                          defaultMessage="Staff Notes"
                        />
                      </FormControl.Label>
                      <TextArea
                        maxLength={TEXT_FIELD_MAX_LENGTH}
                        onChangeText={text => updateOrderEditsByKey('staff_notes', text)}
                        rowSpan={7}
                        value={staff_notes}
                      />
                      <MaxLengthHint
                        maxLength={TEXT_FIELD_MAX_LENGTH}
                        value={staff_notes}
                      />
                    </View>
                  </Stack>
                  {/* Special Instructions */}
                  <Stack
                    style={[Forms.formField]}
                    accessible={false}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <FormControl.Label style={styles.formField__label}>
                        <FormattedMessage
                          id="order__specialInstructions"
                          defaultMessage="Special Instructions"
                          style={{ color: '#000', fontWeight: '600' }}
                        />
                      </FormControl.Label>
                      {order.items.length > 1 && (
                        <Button
                          onPress={() => setShowSpecialInstructions(prev => !prev)}
                          style={{ margin: 0, padding: 0, height: 20 }}
                          variant="ghost"
                        >
                          <FormattedMessage
                            id={showSpecialInstructions ? 'common__hideAll' : 'common__showAll'}
                            defaultMessage={showSpecialInstructions ? 'Hide All' : 'Show All'}
                            style={{ color: Colors.primary, paddingRight: 0 }}
                          />
                        </Button>
                      )}
                    </View>
                    {showSpecialInstructions &&
                      order.items.map((item, index) => (
                        <SpecialInstructionsEditor
                          disabled={!!order.time_closed}
                          edits={orderEdits.items?.find(
                            editedItem => editedItem.orderitemid === item.orderitemid,
                          )}
                          item={item}
                          key={item.orderitemid}
                          maxLength={30}
                          onUpdate={updateOrderItemSpecialInstructions}
                        />
                      ))}
                  </Stack>
                  {/* Remove Items (catering orders only) */}
                  {showRemoveItems && (
                    <Stack
                      style={[Forms.formField]}
                      accessible={false}
                    >
                      <FormControl.Label style={styles.formField__label}>
                        <FormattedMessage
                          id="order__removeItems"
                          defaultMessage="Remove Items"
                        />
                      </FormControl.Label>
                      {order.items.map(item => {
                        const isMarkedForRemoval = itemsToRemoveList.includes(item.orderitemid);
                        const isRefunded = item.status === 'refunded';
                        return (
                          <View
                            key={item.orderitemid}
                            style={styles.removeItemRow}
                          >
                            <View style={{ flex: 1 }}>
                              <Text
                                style={[
                                  styles.removeItemName,
                                  (isMarkedForRemoval || isRefunded) &&
                                    styles.removeItemStrikethrough,
                                ]}
                              >
                                {item.name_when_purchased}
                              </Text>
                              <Text style={styles.removeItemPrice}>
                                ${(item.pretax_cents / 100).toFixed(2)}
                              </Text>
                            </View>
                            {!isRefunded && (
                              <Button
                                testID={`toggle-remove-${item.orderitemid}`}
                                size="sm"
                                variant={isMarkedForRemoval ? 'outline' : 'solid'}
                                colorScheme={isMarkedForRemoval ? 'secondary' : 'danger'}
                                onPress={() => toggleItemRemoval(item.orderitemid)}
                                style={styles.removeItemButton}
                              >
                                <Text
                                  style={
                                    isMarkedForRemoval
                                      ? styles.removeItemUndoText
                                      : styles.removeItemRemoveText
                                  }
                                >
                                  {isMarkedForRemoval ? 'Undo' : 'Remove'}
                                </Text>
                              </Button>
                            )}
                            {isRefunded && (
                              <Text style={styles.removeItemRefundedText}>Refunded</Text>
                            )}
                          </View>
                        );
                      })}
                    </Stack>
                  )}

                  {/* Change Order Date / Time */}

                  <Stack
                    picker
                    stackedLabel
                    style={{ border: 0, marginBottom: 20, marginLeft: 0 }}
                    accessible={false}
                  >
                    <FormControl.Label style={styles.formField__label}>
                      <FormattedMessage
                        id="order__dateTime"
                        defaultMessage="Order Date / Time"
                        style={{ color: '#000', fontWeight: '600' }}
                      />
                    </FormControl.Label>
                    {displayDeliveryJobUpdateWarning()}
                    <TouchableWithoutFeedback
                      disabled={!!order.time_closed}
                      onPress={toggleShowDatetimePicker}
                    >
                      <Text style={[styles.formField__picker, disabledStyle]}>
                        {/* TODO - i18n */}
                        {user_desired_time ? moment(user_desired_time).format('MMM D, LT') : 'ASAP'}
                      </Text>
                    </TouchableWithoutFeedback>
                  </Stack>

                  {/* Change Table / Location */}
                  <Stack
                    picker
                    stackedLabel
                    style={{ border: 0, marginLeft: 0 }}
                    accessible={false}
                  >
                    <FormControl.Label style={styles.formField__label}>
                      <FormattedMessage
                        id="order__tableLocation"
                        defaultMessage="Table / Location"
                        style={{ color: '#000', fontWeight: '600' }}
                      />
                    </FormControl.Label>
                    <Select
                      mode="dropdown"
                      onValueChange={value => updateOrderEditsByKey('location_id', value)}
                      selectedValue={location_id}
                    >
                      {locations.map(location => (
                        <Select.Item
                          key={location.id}
                          label={location.locationName}
                          value={location.id}
                        />
                      ))}
                    </Select>
                  </Stack>

                  {/* Change Server */}
                  <Stack
                    picker
                    stackedLabel
                    style={{ border: 0, marginLeft: 0 }}
                  >
                    <FormControl.Label style={styles.formField__label}>
                      <FormattedMessage
                        id="order__server"
                        defaultMessage="Server"
                        style={{ color: '#000', fontWeight: '600' }}
                      />
                    </FormControl.Label>
                    <Select
                      mode="dropdown"
                      onValueChange={value => updateOrderEditsByKey('bartender_id', value)}
                      selectedValue={String(bartender_id)}
                      placeholder="(Select Server)"
                    >
                      <Select.Item
                        label="(No Server Assigned)"
                        value=""
                      />
                      {bartenders.map(bartender => (
                        <Select.Item
                          key={bartender.id}
                          label={`${bartender.first_name} ${bartender.last_name}`}
                          value={String(bartender.id)}
                        />
                      ))}
                    </Select>
                  </Stack>
                </FormControl>
              </ScrollView>
              <View
                style={[modalStyles.footer, { borderColor: Colors.lightGray, borderTopWidth: 1 }]}
              >
                <Right style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                  <Button
                    variant="ghost"
                    onPress={handleCancel}
                    style={{ marginRight: 10 }}
                  >
                    <FormattedMessage
                      id="common__uppercase__cancel"
                      defaultMessage="Cancel"
                      style={{ color: Colors.primary }}
                    />
                  </Button>
                  <Button
                    testID="edit-order-save-btn"
                    icon
                    onPress={handleSave}
                    disabled={isLoading || allItemsMarkedForRemoval}
                  >
                    {isLoading ? (
                      <ActivityIndicator
                        size="small"
                        style={{ marginHorizontal: 23.5 }}
                        color={Colors.primary}
                      />
                    ) : (
                      <FormattedMessage
                        id="common__uppercase__save"
                        defaultMessage="SAVE"
                        style={{ color: '#FFF' }}
                      />
                    )}
                  </Button>
                </Right>
              </View>
            </View>
          </TouchableWithoutFeedback>
          {!!showDateTimePicker && (
            <DateTimePicker
              is24Hour
              minimumDate={moment().toDate()}
              mode="datetime"
              onChange={updateUserDesiredTime}
              value={offsetInput(user_desired_time || moment())}
            />
          )}
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
}

function MaxLengthHint({ maxLength, value }) {
  return (
    <Text
      style={[
        styles.formField__hint,
        { ...(value?.length === maxLength && { color: Colors.lightTheme.error.regular }) },
      ]}
    >
      {value?.length || 0} / {maxLength}
    </Text>
  );
}

function SpecialInstructionsEditor({ edits, item, maxLength, onUpdate, disabled }) {
  const { itemName, orderitemid, special_instructions } = item;
  const editedInstructions = edits?.special_instructions;
  const initialInputValue =
    editedInstructions === undefined ? special_instructions : editedInstructions;

  const [inputValue, setInputValue] = useState(initialInputValue);
  const [showEditor, setShowEditor] = useState(false);

  const timer = useRef(null); // Prevent memory leaks when user clicks cancel/save while input is focused.

  const handleBlur = () => {
    // Short delay is required to hide the input when the user presses the edit button on blur.
    timer.current = setTimeout(() => {
      setShowEditor(false);
    }, 150);

    if (inputValue !== initialInputValue) {
      onUpdate({
        orderitemid,
        special_instructions: inputValue,
      });
    }
  };

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const disabledStyle = disabled && { color: Colors.disabled };

  return (
    <Stack
      style={[styles.specialInstructionsEditor, { borderBottomWidth: showEditor ? 0 : 1 }]}
      accessible={false}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexGrow: 1,
          width: '95%',
        }}
      >
        <FormControl.Label
          style={[
            styles.specialInstructionsEditor__label,
            disabledStyle,
            { paddingBottom: showEditor || inputValue?.length > 0 ? 0 : 10 },
          ]}
        >
          {itemName}
        </FormControl.Label>
        <TouchableWithoutFeedback
          disabled={disabled || showEditor}
          onPress={() => setShowEditor(true)}
        >
          <View style={{ alignItems: 'center', justifyContent: 'center', paddingVertical: 8 }}>
            <Icon
              as={MaterialIcons}
              name="edit"
              style={[styles.specialInstructionsEditor__icon, disabledStyle]}
            />
          </View>
        </TouchableWithoutFeedback>
      </View>
      {!showEditor && inputValue?.length > 0 && (
        <TouchableWithoutFeedback onPress={() => setShowEditor(true)}>
          <Text style={[styles.specialInstructionsEditor__text]}>{inputValue}</Text>
        </TouchableWithoutFeedback>
      )}
      {showEditor && (
        <Stack
          style={[Forms.formField, { marginBottom: 0, marginTop: 10 }]}
          accessible={false}
        >
          <Input
            autoFocus
            w={{ base: '100%' }}
            maxLength={maxLength}
            onBlur={handleBlur}
            onChangeText={value => setInputValue(value)}
            style={[styles.specialInstructionsEditor__input]}
            value={inputValue}
          />
          <MaxLengthHint
            maxLength={maxLength}
            value={inputValue}
          />
        </Stack>
      )}
    </Stack>
  );
}

const styles = EStyleSheet.create({
  formField__hint: {
    ...Forms.formField__hint,
    alignSelf: 'flex-end',
  },
  formField__label: {
    ...Forms.formField__label,
    color: 'black',
  },
  removeItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.lightGray,
    width: '100%',
  },
  removeItemName: {
    fontSize: 15,
    color: '#000',
  },
  removeItemPrice: {
    fontSize: 13,
    color: Colors.gray1,
    marginTop: 2,
  },
  removeItemStrikethrough: {
    textDecorationLine: 'line-through',
    color: Colors.disabled,
  },
  removeItemButton: {
    minWidth: 80,
  },
  removeItemRemoveText: {
    color: '#FFF',
    fontSize: 13,
  },
  removeItemUndoText: {
    color: Colors.primary,
    fontSize: 13,
  },
  removeItemRefundedText: {
    color: Colors.disabled,
    fontStyle: 'italic',
    fontSize: 13,
  },
  formField__picker: {
    ...Forms.formField__input,
    ...(Platform.OS === 'android' && { marginTop: 4 }),
    ...(Platform.OS === 'web' && { backgroundColor: 'transparent' }), // Omit on Android to preserve dropdown chevron.
    alignSelf: 'flex-start',
    borderColor: Colors.lightGray,
    borderBottomColor: Colors.gray1,
    width: '100%',
  },
  specialInstructionsEditor: {
    alignItems: 'flex-start',
    borderColor: Colors.gray1,
    flexDirection: 'column',
    marginLeft: 0,
    paddingTop: 10,
    width: '100%',
  },
  specialInstructionsEditor__icon: {
    fontSize: 20,
    marginBottom: 0,
    paddingLeft: 10,
  },
  specialInstructionsEditor__input: {
    ...Forms.formField__input,
    borderColor: Colors.lightGray,
    borderBottomColor: Colors.gray1,
    paddingRight: 10,
    fontSize: 15,
    paddingLeft: 10,
  },
  specialInstructionsEditor__label: {
    ...Forms.formField__label,
    color: 'black',
    flexShrink: 1,
    fontWeight: 'normal',
    marginBottom: 0,
    paddingRight: 10,
    paddingTop: 10,
  },
  specialInstructionsEditor__text: {
    color: Colors.gray1,
    fontStyle: 'italic',
    padding: 10,
    paddingLeft: 10,
    width: '100%',
  },
});

export default EditOrderModal;
